import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { EmployeeDashboard as EmployeeDashboardType } from '@/types';
import { LeaveTypeBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { CalendarDays, Clock, CheckCircle2, AlertCircle, ArrowRight, Activity, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export default function EmployeeDashboard() {
  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'employee'],
    queryFn: async () => {
      const res = await dashboardApi.employee();
      return res.data.data as EmployeeDashboardType;
    },
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            {format(new Date(), 'EEEE, MMMM do, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/leaves/my">
            <Button variant="secondary" className="shadow-sm">View My Leaves</Button>
          </Link>
          <Link to="/leaves/apply">
            <Button id="tour-apply" className="shadow-sm shadow-blue-500/20">Apply Leave</Button>
          </Link>
        </div>
      </div>

      {/* Leave Balances Grid */}
      {(() => {
        // For the "Total Leaves" card, we exclude WFH as it's unlimited.
        const quotaBalances = data.balances.filter((balance: any) =>
          balance.leaveType?.code !== 'WFH'
        );

        const totalAvailable = quotaBalances.reduce((sum: number, b: any) => sum + (b.available ?? 0), 0);
        const totalUsed = quotaBalances.reduce((sum: number, b: any) => sum + (b.used ?? 0), 0);
        const totalDays = quotaBalances.reduce((sum: number, b: any) => sum + (b.total + (b.carriedOver ?? 0)), 0);
        const totalUtilisation = totalDays > 0 ? Math.round((totalUsed / totalDays) * 100) : 0;

        return (
          <div id="tour-balances" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {/* Total Summary Card — always first */}
            <div className="card p-5 animate-fade-in hover:shadow-elevated transition-shadow"
              style={{
                background: isDark
                  ? 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)'
                  : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                border: `1.5px solid ${isDark ? '#3b82f6' : '#93c5fd'}`,
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <h3 className="font-semibold" style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>Total Leaves</h3>
                </div>
                <div className="px-2 py-1 rounded text-xs font-semibold"
                  style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#bfdbfe', color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                  ALL
                </div>
              </div>

              <div className="flex items-end gap-2 mb-4">
                <span className="text-4xl font-bold tracking-tight" style={{ color: isDark ? '#60a5fa' : '#1d4ed8' }}>{totalAvailable}</span>
                <span className="text-sm font-medium mb-1" style={{ color: isDark ? '#93c5fd' : '#3b82f6' }}>days left</span>
              </div>

              <div className="w-full rounded-full h-2 mb-2 overflow-hidden" style={{ backgroundColor: isDark ? '#1e3a5f' : '#bfdbfe' }}>
                <div
                  className="h-2 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${Math.min(100, totalUtilisation)}%`, backgroundColor: '#3b82f6' }}
                />
              </div>

              <div className="flex justify-between text-xs" style={{ color: isDark ? '#93c5fd' : '#3b82f6' }}>
                <span>Used: {totalUsed}</span>
                <span>Total: {totalDays}</span>
              </div>
            </div>

            {/* Individual leave type cards */}
            {data.balances.map((balance: any, i: number) => {
              const isWfh = balance.leaveType.code === 'WFH';
              const isLow = balance.utilisation > 80;
              const isMedium = balance.utilisation > 50;
              const progressColor = isWfh ? '#8b5cf6' : (isLow ? '#ef4444' : isMedium ? '#f59e0b' : '#22c55e');

              return (
                <div
                  key={balance.id}
                  className="card p-5 animate-fade-in hover:shadow-elevated transition-shadow"
                  style={{ animationDelay: `${(i + 1) * 50}ms` }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: balance.leaveType.colour }} />
                      <h3 className="font-semibold text-slate-700">{balance.leaveType.name}</h3>
                    </div>
                    <div className="px-2 py-1 rounded text-xs font-semibold"
                      style={{
                        backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
                        color: isDark ? '#94a3b8' : '#475569',
                      }}>
                      {balance.leaveType.code}
                    </div>
                  </div>

                  {isWfh ? (
                    <>
                      <div className="flex items-end gap-2 mb-4">
                        <span className="text-4xl font-bold text-slate-800 tracking-tight">{balance.used}</span>
                        <span className="text-sm font-medium text-slate-500 mb-1">days taken</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `100%`, backgroundColor: progressColor, opacity: 0.5 }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span />
                        {balance.pending > 0 && <span className="text-amber-600 font-medium">Pending: {balance.pending}</span>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-end gap-2 mb-4">
                        <span className="text-4xl font-bold text-slate-800 tracking-tight">{balance.available}</span>
                        <span className="text-sm font-medium text-slate-500 mb-1">days left</span>
                      </div>

                      <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden">
                        <div
                          className="h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${Math.min(100, balance.utilisation)}%`, backgroundColor: progressColor }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Used: {balance.used}</span>
                        {balance.pending > 0 && <span className="text-amber-600 font-medium">Pending: {balance.pending}</span>}
                        <span>Total: {balance.total + balance.carriedOver}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Chart */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center gap-2 mb-6">
              <Activity className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-slate-800">Leave Usage (Last 12 Months)</h2>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthlyUsage} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: isDark ? '#94a3b8' : '#64748b' }} />
                  <Tooltip
                    cursor={{ fill: isDark ? '#253047' : '#f1f5f9' }}
                    contentStyle={{
                      borderRadius: '12px',
                      border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
                      backgroundColor: isDark ? '#1e293b' : '#ffffff',
                      color: isDark ? '#e2e8f0' : '#1e293b',
                    }}
                    labelStyle={{ color: isDark ? '#94a3b8' : '#64748b' }}
                  />
                  <Bar dataKey="days" radius={[4, 4, 0, 0]}>
                    {data.monthlyUsage.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.days > 0 ? '#3b82f6' : isDark ? '#334155' : '#e2e8f0'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right Column - Reports To + Upcoming Leaves + Recent Activity */}
        <div className="space-y-6">

          {/* Reports To */}
          {(() => {
            const manager = user?.manager;
            const managerInitials = manager?.name
              ? manager.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
              : 'N/A';
            return (
              <div className="card p-5 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                <h2 className="text-sm font-bold mb-4 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  Reports To
                </h2>
                <div className="flex items-center gap-4">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-10 h-10 rounded-full bg-slate-700 border-2 border-slate-600 flex items-center justify-center mb-1">
                      {user?.photoUrl ? (
                        <img src={user.photoUrl} alt="You" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold">{user?.name?.split(' ').map((n: string) => n[0]).join('').slice(0,2).toUpperCase() ?? 'You'}</span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400">{user?.name?.split(' ')[0] ?? 'You'}</span>
                  </div>
                  <div className="flex-1 h-0.5 bg-slate-700 mb-4" />
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-900/50 border-2 border-blue-500 flex items-center justify-center mb-1 overflow-hidden">
                      {manager?.photoUrl ? (
                        <img src={manager.photoUrl} alt={manager.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[10px] font-bold text-blue-200">{managerInitials}</span>
                      )}
                    </div>
                    <span className="text-[10px] font-medium text-slate-300 text-center max-w-[80px] truncate">
                      {manager?.name ?? 'Not Assigned'}
                    </span>
                    <span className="text-[10px] text-slate-500">{manager?.designation ?? 'Manager'}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Upcoming Leaves */}
          <div className="card p-5">
            <h2 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-blue-600" />
              Upcoming Leaves
            </h2>
            {data.upcomingLeaves.length === 0 ? (
              <div className="text-center py-5">
                <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CalendarDays className="w-5 h-5 text-slate-400" />
                </div>
                <p className="text-xs text-slate-500">No upcoming approved leaves</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                {data.upcomingLeaves.map(leave => (
                  <div key={leave.id} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: isDark ? '#253047' : '#f8fafc', border: `1px solid ${isDark ? '#334155' : '#f1f5f9'}` }}>
                    <div className="w-9 h-9 rounded-lg flex flex-col items-center justify-center shrink-0" style={{ backgroundColor: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${isDark ? '#475569' : '#e2e8f0'}` }}>
                      <span className="text-[10px] font-bold text-slate-500">{format(new Date(leave.fromDate), 'MMM')}</span>
                      <span className="text-xs font-black text-slate-800">{format(new Date(leave.fromDate), 'd')}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-700 text-xs">{leave.leaveType.name}</h4>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {leave.duration} day{leave.duration !== 1 ? 's' : ''} • {format(new Date(leave.toDate), 'MMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                Recent Activity
              </h2>
              {data.pendingLeaves > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                  {data.pendingLeaves} pending
                </span>
              )}
            </div>
            <div className="space-y-0 relative before:absolute before:inset-y-0 before:left-[11px] before:w-[2px] before:bg-slate-100">
              {data.recentLeaves.map((leave, i) => (
                <div key={leave.id} className="relative pl-8 py-3">
                  <div className={`absolute left-0 top-4 w-6 h-6 rounded-full border-2 flex items-center justify-center ${leave.status === 'APPROVED' ? 'bg-green-500' : 'bg-red-500'}`} style={{ borderColor: isDark ? '#1e293b' : '#ffffff' }}>
                    {leave.status === 'APPROVED' ? <CheckCircle2 className="w-3.5 h-3.5 text-white" /> : <AlertCircle className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="p-3 rounded-lg shadow-sm" style={{ backgroundColor: isDark ? '#253047' : '#ffffff', border: `1px solid ${isDark ? '#334155' : '#f1f5f9'}` }}>
                    <div className="flex justify-between items-start mb-1">
                      <LeaveTypeBadge colour={leave.leaveType.colour} name={leave.leaveType.name} />
                      <span className="text-[11px] text-slate-400">{format(new Date(leave.appliedAt), 'MMM d')}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-700 mt-1">
                      {format(new Date(leave.fromDate), 'MMM d')} - {format(new Date(leave.toDate), 'MMM d')}
                    </p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">{leave.reason}</p>
                  </div>
                </div>
              ))}
              {data.recentLeaves.length === 0 && (
                <p className="pl-8 text-xs text-slate-500">No recent leave activity</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
